import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import type { User, AuthState } from '@/types';
import { api } from '@/services/api';

interface AuthContextValue extends AuthState {
  isLoading: boolean;
  login: (email: string, password: string, mdaCode?: string, isPlatformLogin?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  completeLogin: (accessToken: string, refreshToken: string) => void;
  clearMustChangePassword: () => void;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const AUTH_STORAGE_KEY = 'ohcs_auth';

interface StoredAuth {
  accessToken: string;
  refreshToken: string;
  user: User;
  isPlatformAdmin: boolean;
  mdaCode?: string;
  mustChangePassword?: boolean;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    accessToken: null,
    refreshToken: null,
    isAuthenticated: false,
    isPlatformAdmin: false,
    mustChangePassword: false,
  });
  const [isLoading, setIsLoading] = useState(true);

  // Load auth from storage on mount
  useEffect(() => {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (stored) {
      try {
        const parsed: StoredAuth = JSON.parse(stored);
        setState({
          user: parsed.user,
          accessToken: parsed.accessToken,
          refreshToken: parsed.refreshToken,
          isAuthenticated: true,
          isPlatformAdmin: parsed.isPlatformAdmin,
          mustChangePassword: parsed.mustChangePassword || false,
        });
        api.setAuthToken(parsed.accessToken);
        api.setRefreshToken(parsed.refreshToken);
        api.setIsPlatformAdmin(parsed.isPlatformAdmin);
        if (parsed.mdaCode) {
          api.setMdaCode(parsed.mdaCode);
        }
      } catch {
        localStorage.removeItem(AUTH_STORAGE_KEY);
      }
    }
    setIsLoading(false);
  }, []);

  const saveAuth = (auth: StoredAuth) => {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(auth));
  };

  const clearAuth = () => {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    api.setAuthToken(null);
    api.setMdaCode(null);
  };

  const login = useCallback(async (email: string, password: string, mdaCode?: string, isPlatformLogin: boolean = false) => {
    const endpoint = isPlatformLogin ? '/api/platform/auth/login' : '/api/v1/auth/login';
    const credentials = isPlatformLogin ? { email, password } : { email, password, mdaCode };

    const response = await api.post<{
      accessToken: string;
      refreshToken: string;
      user: User & { isSuperAdmin?: boolean };
      mda?: { id: string; code: string; name: string };
      mustChangePassword?: boolean;
    }>(endpoint, credentials);

    if (!response.data) {
      throw new Error(response.error?.message || 'Login failed');
    }

    const { accessToken, refreshToken, user, mda, mustChangePassword } = response.data;

    api.setAuthToken(accessToken);
    api.setRefreshToken(refreshToken);
    api.setIsPlatformAdmin(isPlatformLogin);
    if (mda) {
      api.setMdaCode(mda.code);
    }

    const authUser: User = {
      id: user.id,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      roles: user.roles || [],
      permissions: user.permissions || (isPlatformLogin ? ['admin:all'] : []),
      securityClearance: user.securityClearance || 5,
    };

    setState({
      user: authUser,
      accessToken,
      refreshToken,
      isAuthenticated: true,
      isPlatformAdmin: isPlatformLogin,
      mustChangePassword: mustChangePassword || false,
    });

    saveAuth({
      accessToken,
      refreshToken,
      user: authUser,
      isPlatformAdmin: isPlatformLogin,
      mdaCode: mda?.code,
      mustChangePassword: mustChangePassword || false,
    });
  }, []);

  const logout = useCallback(async () => {
    const endpoint = state.isPlatformAdmin ? '/api/platform/auth/logout' : '/api/v1/auth/logout';

    try {
      await api.post(endpoint, { refreshToken: state.refreshToken });
    } catch {
      // Continue with logout even if API call fails
    }

    clearAuth();
    setState({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isPlatformAdmin: false,
      mustChangePassword: false,
    });
  }, [state.isPlatformAdmin, state.refreshToken]);

  const clearMustChangePassword = useCallback(() => {
    setState(prev => ({
      ...prev,
      mustChangePassword: false,
    }));

    // Update stored auth
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        saveAuth({ ...parsed, mustChangePassword: false });
      } catch {
        // Ignore parsing errors
      }
    }
  }, []);

  const refreshAuth = useCallback(async () => {
    if (!state.refreshToken) return;

    const endpoint = state.isPlatformAdmin ? '/api/platform/auth/refresh' : '/api/v1/auth/refresh';
    const body = state.isPlatformAdmin
      ? { refreshToken: state.refreshToken }
      : { refreshToken: state.refreshToken, mdaCode: api.getMdaCode() };

    try {
      const response = await api.post<{ accessToken: string; refreshToken: string }>(endpoint, body);

      if (response.data) {
        const { accessToken, refreshToken } = response.data;
        api.setAuthToken(accessToken);

        setState(prev => ({
          ...prev,
          accessToken,
          refreshToken,
        }));

        const stored = localStorage.getItem(AUTH_STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          saveAuth({ ...parsed, accessToken, refreshToken });
        }
      }
    } catch {
      // Refresh failed, logout
      await logout();
    }
  }, [state.refreshToken, state.isPlatformAdmin, logout]);

  const hasPermission = useCallback((permission: string) => {
    if (!state.user) return false;
    if (state.user.permissions.includes('admin:all')) return true;
    return state.user.permissions.includes(permission);
  }, [state.user]);

  const hasAnyPermission = useCallback((permissions: string[]) => {
    if (!state.user) return false;
    if (state.user.permissions.includes('admin:all')) return true;
    return permissions.some(p => state.user!.permissions.includes(p));
  }, [state.user]);

  const hasAllPermissions = useCallback((permissions: string[]) => {
    if (!state.user) return false;
    if (state.user.permissions.includes('admin:all')) return true;
    return permissions.every(p => state.user!.permissions.includes(p));
  }, [state.user]);

  // Complete login after 2FA verification - updates tokens from 2FA response
  const completeLogin = useCallback((accessToken: string, refreshToken: string) => {
    api.setAuthToken(accessToken);
    api.setRefreshToken(refreshToken);

    setState(prev => ({
      ...prev,
      accessToken,
      refreshToken,
      isAuthenticated: true,
    }));

    // Update stored auth with new tokens
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        saveAuth({ ...parsed, accessToken, refreshToken });
      } catch {
        // If parsing fails, clear and user will need to re-login
        clearAuth();
      }
    }
  }, []);

  if (isLoading) {
    return null; // Or a loading spinner
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        isLoading,
        login,
        logout,
        refreshAuth,
        completeLogin,
        clearMustChangePassword,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
